import {
  $tracksTableSearchInputValue,
  TracksTable,
  TracksTableFiltersMenu,
  TracksTableGate,
  tracksTableQuery,
  tracksTableSearchChanged,
} from '@/entities/tracks'
import { ROUTES, STRINGS } from '@/shared/config'
import { isValidTab } from '@/shared/lib/tabs'
import {
  BorderlessTabs,
  EmptyContent,
  HeaderContent,
  HidingSpin,
} from '@/shared/ui'
import { ContentWrapper } from '@/shared/ui'
import { FilterOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, Layout, Popover, Space } from 'antd'
import { useUnit } from 'effector-react'
import { NavLink, useNavigate } from 'react-router-dom'

import { addNewMessages, emptyListMessages } from './lib/strings'
import {
  type TracksListPageTabsKeys,
  TracksListPageTabsKeysArray,
  TracksListPageTabsKeysEnum,
} from './lib/tabs'
import {
  $tracksListPageLatestFilters,
  $tracksListPageLatestPage,
  $tracksListPageLatestPageSize,
  $tracksListPageLatestSearch,
  $tracksListPageLatestSort,
  $tracksListPageTab,
  TracksListPageGate,
  tracksListPageTabChanged,
} from './model/tracks-list.model'

export function TracksListPage() {
  const navigate = useNavigate()
  const changeSearch = useUnit(tracksTableSearchChanged)
  const search = useUnit($tracksTableSearchInputValue) || undefined
  const latestSearch = useUnit($tracksListPageLatestSearch)
  const latestSort = useUnit($tracksListPageLatestSort)
  const latestFilters = useUnit($tracksListPageLatestFilters)
  const latestPage = useUnit($tracksListPageLatestPage)
  const latestPageSize = useUnit($tracksListPageLatestPageSize)
  const { data, pending, error } = useUnit(tracksTableQuery)
  const tab = useUnit($tracksListPageTab)
  const changeTab = useUnit(tracksListPageTabChanged)

  const handleTabChange = (newTab: string) => {
    if (
      !isValidTab<TracksListPageTabsKeys>(newTab, TracksListPageTabsKeysArray)
    )
      return
    changeTab(newTab)
  }

  return (
    <Layout>
      <TracksListPageGate />
      <TracksTableGate
        initialFilters={latestFilters}
        initialPage={latestPage}
        initialPageSize={latestPageSize}
        initialSort={latestSort}
        initialSearch={latestSearch}
      />
      <HeaderContent
        title="Аудиофайлы"
        description="Здесь можно посмотреть каталоги музыки, подкастов, проповедей и аудиокниг и управлять ими, а также видеть, создавать, обновлять и удалять контент"
        buttonSlot={
          <NavLink to={ROUTES.AUDIO.CREATE}>
            <Button color="purple" variant="solid" icon={<PlusOutlined />}>
              Создать
            </Button>
          </NavLink>
        }
      />
      <ContentWrapper verticalPadding="small">
        <BorderlessTabs
          activeKey={tab}
          onChange={handleTabChange}
          destroyOnHidden
          tabBarExtraContent={
            <Space>
              <Popover
                content={<TracksTableFiltersMenu />}
                title={STRINGS.label_filters_control}
                trigger="click"
                placement="bottom"
                destroyOnHidden
              >
                <Button icon={<FilterOutlined />}>
                  {STRINGS.label_filters_control}
                </Button>
              </Popover>
              <Input
                placeholder={STRINGS.placeholder_search_input}
                allowClear
                addonAfter={<SearchOutlined />}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const searchQueryString = event.target.value
                  changeSearch(searchQueryString)
                }}
                style={{
                  minWidth: 300,
                }}
                value={search}
              />
            </Space>
          }
          items={[
            {
              label: 'Музыка',
              key: TracksListPageTabsKeysEnum.music,
            },
            {
              label: 'Подкасты',
              key: TracksListPageTabsKeysEnum.podcast,
            },
            {
              label: 'Проповеди',
              key: TracksListPageTabsKeysEnum.sermon,
            },
            {
              label: 'Аудиокниги',
              key: TracksListPageTabsKeysEnum.audiobook,
            },
          ]}
        />

        <HidingSpin spinning={pending || (!data?.items && !error)}>
          {data?.items && data.items.length > 0 ? (
            <TracksTable
              onEdit={(trackId: string) => {
                void navigate(ROUTES.AUDIO.EDIT.replace(':id', trackId))
              }}
            />
          ) : search?.length && search?.length > 0 ? (
            <EmptyContent description={STRINGS.nothing_was_found} />
          ) : (
            <EmptyContent
              description={emptyListMessages[tab]}
              content={
                <Space>
                  <NavLink to={ROUTES.AUDIO.CREATE_WITH_TYPE(tab)}>
                    <Button>{addNewMessages[tab]}</Button>
                  </NavLink>
                </Space>
              }
            />
          )}
        </HidingSpin>
      </ContentWrapper>
    </Layout>
  )
}
